import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'

import StepForm from '../../../../../../features/components/Forms/StepForm'
import LineItemStep from './Steps/LineItemStep'
import AudiencesStep from './Steps/AudiencesStep'

import { useLineItemGoogleCreate } from './useLineItemGoogleCreate'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { clearLineItems } from '../../../../../../modules/actions/lineItems'
import { setRightUploadAdData } from '../../../../../../modules/actions/rights'
import {
  lineItemCreatedDataSelector,
  lineItemsSelector,
  lineItemWasCreatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { rightSelector } from '../../../../../../modules/selectors/rights'

import { getValidationSchema, getInitialValues } from './fields'
import { LINE_ITEM_FORM } from '../../index'
import { GOOGLE_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { DIGITAL_ALLOCATION_TYPE_BUDGET } from '../../../../../Multiplatform/Partnerships/RightForms/fields'
import { LINE_ITEM } from '../../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/fields'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../../hooks/formHooks/listItemFormHooks/fields'

const LineItemForm = ({ onSuccessSubmit, formProgressItems }) => {
  const dispatch = useDispatch()

  const [manualSuccessSubmit, setManualSuccessSubmit] = useState(false)

  // line items are loaded when submitting previous CampaignForm with existing campaign option
  const lineItems = useSelector(lineItemsSelector)
  const lineItemWasCreated = useSelector(lineItemWasCreatedSelector)
  const lineItemFacebookCreatedData = useSelector(lineItemCreatedDataSelector)
  const right = useSelector(rightSelector)

  const { allocation_type: allocationTypeOnDigitalRight } = right

  const isRightAllocationBudget = allocationTypeOnDigitalRight === DIGITAL_ALLOCATION_TYPE_BUDGET

  const isLineItemsExist = !!lineItems.length

  const {
    onSubmit: onLineItemCreateSubmit,
    onClearSubmit,
    isLoadingSelector,
    errorSelector,
    successSubmit
  } = useLineItemGoogleCreate()

  const onSubmit = useCallback(
    values => {
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedLineItemFullData = lineItems.find(lineItem => values[LINE_ITEM] === lineItem.id)

        dispatch(setRightUploadAdData({ lineItem: selectedLineItemFullData }, GOOGLE_PLATFORM))

        setManualSuccessSubmit(true)
      } else {
        // use double request line item creation from useLineItemGoogleCreate hook
        onLineItemCreateSubmit(values)
      }
    },
    [dispatch, lineItems, onLineItemCreateSubmit]
  )

  const formik = useFormik({
    initialValues: getInitialValues(isLineItemsExist),
    validationSchema: getValidationSchema({ isRightAllocationBudget }),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  const showOnlyLineItemStep = values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM

  const steps = useMemo(
    () => [
      {
        component: (
          <LineItemStep isOnlyLineItemStep={showOnlyLineItemStep} isRightAllocationBudget={isRightAllocationBudget} />
        ),
        show: true
      },
      {
        component: <AudiencesStep />,
        show: !showOnlyLineItemStep
      }
    ],
    [showOnlyLineItemStep, isRightAllocationBudget]
  )

  useEffect(() => {
    if (lineItemWasCreated) {
      dispatch(setRightUploadAdData({ lineItem: lineItemFacebookCreatedData }, GOOGLE_PLATFORM))
    }
  }, [dispatch, lineItemWasCreated, lineItemFacebookCreatedData])

  useEffect(
    () => () => {
      dispatch(clearLineItems())
    },
    [dispatch]
  )

  return (
    <StepForm
      steps={steps}
      formName={LINE_ITEM_FORM}
      formik={formik}
      submitText="Save and continue"
      formProgressItems={formProgressItems}
      clearSubmitHandler={onClearSubmit}
      errorSelector={errorSelector}
      isLoadingSelector={isLoadingSelector}
      successSubmit={manualSuccessSubmit || successSubmit}
      onSuccessSubmit={onSuccessSubmit}
    />
  )
}

export default LineItemForm
